import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../../../contexts/auth-context'
import { useVoiceCallSession } from '../../../hooks/use-voice-call-session'
import { isNativeApp } from '../../../utils/platform'
import { getVoiceCallSession, isTerminalCallPhase } from '../../../stores/voice-call-store'
import {
  enableBiometricUnlock,
  getBiometricAvailability,
  isBiometricUnlockEnabled,
  verifyBiometric,
} from '../../../services/biometric-auth-service'
import {
  evaluateSoftLockOnForeground,
  isAppLocked,
  recordAppBackgrounded,
  setAppLocked,
  useIsAppLocked,
  whenSoftLockClocksHydrated,
} from '../../../stores/app-lock-store'
import {
  POST_CALL_PROMPT_GRACE_MS,
  SOFT_LOCK_PROMPT_REASON,
  canOfferOptionalBiometricPrompt,
  requiresMandatoryBiometricSoftLock,
} from '../session-security-policy'
import { registerSoftLockUnlock } from '../soft-lock-bridge'
import { decideSoftLockAfterProbe } from '../soft-lock-probe-decision'

const L1_L2_PROMPT_SKIPPED_KEY = 'ascend-soft-lock-optional-prompt-skipped'

/**
 * Synchronous live-call check (19D). The reactive `suppressSheetForCall` can
 * lag the store by a render — e.g. a CallKit/VoIP-launched cold start where
 * the lock evaluates before the incoming session lands — so the prompt path
 * re-reads the store directly to guarantee the OS biometric sheet never opens
 * over incoming-call UI. Outbound/send stay gated while locked mid-call.
 */
function hasLiveCall(): boolean {
  const session = getVoiceCallSession()
  return !!session && !isTerminalCallPhase(session.phase)
}

/**
 * Drives the native soft-lock: L3+ mandatory biometric every 12h; optional
 * opt-in for others who enabled it in settings. Checks on cold start and
 * return-from-background. Cognito session/tokens are untouched.
 *
 * While a non-terminal call is active (including Switch / elsewhere), the
 * unlock sheet is suppressed so Answer/Switch stay usable and the OS
 * biometric prompt can never cover the call UI (19D) — but the locked flag
 * remains, and the app chrome stays fully gated (inert/aria-hidden/
 * pointer-events-none via appChromeGateProps) for the whole call: a locked
 * user gets the call experience and nothing else. When the call ends the
 * sheet reappears and the auto-prompt fires once, resuming normal unlock.
 */
export function useAppLock() {
  const { user, signOut } = useAuth()
  const callSession = useVoiceCallSession()
  const [policyActive, setPolicyActive] = useState(false)
  const [needsEnroll, setNeedsEnroll] = useState(false)
  const [optionalPrompt, setOptionalPrompt] = useState(false)
  const [label, setLabel] = useState('Biometrics')
  const [checking, setChecking] = useState(false)
  const [failed, setFailed] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const locked = useIsAppLocked()
  const checkingRef = useRef(false)
  const [probeSettled, setProbeSettled] = useState(false)

  // Hide unlock sheet while any non-terminal call phase is active so Answer /
  // Decline / Switch / End stay tappable above the soft-lock (13B / 19D).
  const suppressSheetForCall =
    !!callSession && !isTerminalCallPhase(callSession.phase)

  // A call just ended while the app is still locked: hold the AUTOMATIC prompt
  // for a short grace so the OS biometric/passcode sheet doesn't ambush the
  // person the instant they hang up. Only the auto-prompt waits — the lock
  // cover, chrome gating and the Unlock button are untouched, and the user can
  // unlock immediately by tapping it.
  const [postCallHold, setPostCallHold] = useState(false)
  // Adjusted during render (not in an effect) so the auto-prompt effect below
  // already sees the hold in the very commit where the call ended.
  const [prevInCall, setPrevInCall] = useState(suppressSheetForCall)
  if (prevInCall !== suppressSheetForCall) {
    setPrevInCall(suppressSheetForCall)
    setPostCallHold(prevInCall && !suppressSheetForCall && isAppLocked())
  }
  useEffect(() => {
    if (!postCallHold) return
    const id = setTimeout(() => setPostCallHold(false), POST_CALL_PROMPT_GRACE_MS)
    return () => clearTimeout(id)
  }, [postCallHold])

  // Resolve policy whenever the signed-in user changes; evaluate 12h clock
  // (covers cold start + process relaunch after background kill).
  // Web weekly MFA owns the locked flag on browsers — do not clear it here.
  useEffect(() => {
    if (!isNativeApp() || !user) {
      setPolicyActive(false)
      setNeedsEnroll(false)
      setOptionalPrompt(false)
      setProbeSettled(false)
      if (isNativeApp()) setAppLocked(false)
      return
    }
    const mandatory = requiresMandatoryBiometricSoftLock(user)
    const optionalOffer = canOfferOptionalBiometricPrompt(user)
    // Cover the app immediately for overdue L3+ — don't wait on isAvailable
    // (up to 4s) with an interactive Messages list behind a missing sheet.
    if (mandatory && evaluateSoftLockOnForeground()) {
      setPolicyActive(true)
      setAppLocked(true)
    }
    setProbeSettled(false)
    let cancelled = false
    void (async () => {
      // Both probes are time-boxed (4s). A hung isAvailable() must not stall
      // this path before verifyIdentity is even invoked — see #290. The clock
      // hydration (also time-boxed) must land BEFORE the decision below reads
      // the 12h clock: deciding from an un-hydrated localStorage after a
      // force-kill treated a within-window user as overdue and re-prompted on
      // every cold start. The early cover above stays — if hydration proves
      // the clock fresh, the sheet clears without ever prompting.
      const [unlockEnabled, availability] = await Promise.all([
        isBiometricUnlockEnabled(),
        getBiometricAvailability({ allowPasscodeFallback: true }),
        whenSoftLockClocksHydrated(),
      ])
      if (cancelled) return
      setLabel(availability.label)

      const decision = decideSoftLockAfterProbe({
        mandatory,
        optionalOffer,
        unlockEnabled,
        availability,
      })
      setPolicyActive(decision.policyActive)
      setNeedsEnroll(decision.needsEnroll)
      setFailed(decision.failed)
      setErrorMessage(decision.errorMessage)
      if (decision.evaluateClock) {
        setAppLocked(evaluateSoftLockOnForeground())
      } else {
        setAppLocked(decision.lock)
      }

      // L1/L2: one soft prompt per session (29B) — Skip dismisses for this session.
      if (decision.optionalPrompt) {
        try {
          if (sessionStorage.getItem(L1_L2_PROMPT_SKIPPED_KEY) !== '1') {
            setOptionalPrompt(true)
          } else {
            setOptionalPrompt(false)
          }
        } catch {
          setOptionalPrompt(true)
        }
      } else {
        setOptionalPrompt(false)
      }
      setProbeSettled(true)
    })()
    return () => {
      cancelled = true
    }
  }, [user?.userId, user?.roleType, user?.providerLevel])

  const attemptUnlock = useCallback(async (): Promise<boolean> => {
    if (checkingRef.current) return false
    // Never open the OS biometric sheet over live call UI (13B / 19D) — the
    // unlock sheet is suppressed mid-call, so a prompt here could only have
    // come from the cold-start race or a gated outbound action; both stay
    // blocked until the call ends.
    if (hasLiveCall()) return false
    checkingRef.current = true
    setChecking(true)
    setFailed(false)
    setErrorMessage(null)
    try {
      if (needsEnroll && user) {
        const result = await enableBiometricUnlock(user.email ?? user.userId, { label })
        if (result.ok) {
          setNeedsEnroll(false)
          return true
        }
        setFailed(true)
        setErrorMessage(
          result.cancelled
            ? `Verification was cancelled. Enable ${label} to continue, or use your password.`
            : result.message,
        )
        return false
      }
      const result = await verifyBiometric(SOFT_LOCK_PROMPT_REASON, { label })
      if (result.ok) return true
      setFailed(true)
      setErrorMessage(
        result.cancelled
          ? `Verification was cancelled. Try again or use your password.`
          : result.message,
      )
      return false
    } finally {
      checkingRef.current = false
      setChecking(false)
    }
  }, [needsEnroll, user, label])

  // A successful unlock — fast, late (after the hang budget), or via another
  // surface like step-up — must reset the sheet's error state: `failed` gates
  // the auto-prompt, and stale "didn't respond" copy on the NEXT 12h lock
  // reads as broken biometrics.
  //
  // Deliberately NO second sheet-owned hang budget here: force-clearing
  // `checkingRef` while the native prompt was still up is what allowed a
  // second concurrent verifyIdentity() to stack (the build-91 re-verify loop /
  // build-57 freeze). The service's own timeout already flips `checking`
  // within the 4s budget — it is pure JS and always settles.
  useEffect(() => {
    if (!locked) {
      setFailed(false)
      setErrorMessage(null)
    }
  }, [locked])

  useEffect(() => {
    if (!isNativeApp()) return
    registerSoftLockUnlock(attemptUnlock)
    return () => registerSoftLockUnlock(null)
  }, [attemptUnlock])

  // Auto-prompt when soft-locked (unless a live call owns the screen). Wait
  // until the availability probe has settled so we don't call verifyIdentity
  // before we know enroll/hang state — the sheet is already covering the app.
  // The extra synchronous hasLiveCall() read closes the cold-start race where
  // the incoming call session lands after `locked` flips but before this effect.
  useEffect(() => {
    if (
      locked &&
      policyActive &&
      probeSettled &&
      !checking &&
      !failed &&
      !suppressSheetForCall &&
      !postCallHold &&
      !hasLiveCall()
    ) {
      void attemptUnlock()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, policyActive, probeSettled, suppressSheetForCall, postCallHold])

  // Track background/foreground; re-check 12h clock on every return.
  useEffect(() => {
    if (!isNativeApp() || !policyActive) return
    let remove: (() => void) | null = null
    let cancelled = false
    void import('@capacitor/app').then(({ App }) =>
      App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          // OS biometric sheet can momentarily background the app — ignore mid-unlock.
          if (!checkingRef.current) recordAppBackgrounded()
        } else if (!checkingRef.current) {
          if (evaluateSoftLockOnForeground()) setAppLocked(true)
        }
      }).then((handle) => {
        if (cancelled) void handle.remove()
        else remove = () => void handle.remove()
      }),
    )
    return () => {
      cancelled = true
      remove?.()
    }
  }, [policyActive])

  const dismissOptionalPrompt = useCallback(() => {
    try {
      sessionStorage.setItem(L1_L2_PROMPT_SKIPPED_KEY, '1')
    } catch {
      /* best-effort */
    }
    setOptionalPrompt(false)
  }, [])

  const acceptOptionalPrompt = useCallback(async () => {
    if (!user) return
    setChecking(true)
    const result = await enableBiometricUnlock(user.email ?? user.userId, { label })
    setChecking(false)
    if (result.ok) {
      setOptionalPrompt(false)
      setPolicyActive(true)
    } else {
      setFailed(true)
      setErrorMessage(result.message)
    }
  }, [user, label])

  // Fail-closed sign-out from the lock sheet ("Use password instead"). The
  // screen fires it as `void signOut()`, so a rejection would vanish — catch
  // here and surface it on the sheet. On failure the soft-lock clocks are
  // untouched (auth-context clears them only after a confirmed teardown), so
  // the user simply stays locked.
  const signOutFromLock = useCallback(async () => {
    try {
      await signOut()
    } catch {
      setFailed(true)
      setErrorMessage('Sign-out failed. Please try again.')
    }
  }, [signOut])

  const showSheet =
    isNativeApp() &&
    Boolean(user) &&
    policyActive &&
    locked &&
    !suppressSheetForCall

  return {
    locked: isNativeApp() && Boolean(user) && policyActive && locked,
    showSheet,
    checking,
    failed,
    errorMessage,
    label,
    needsEnroll,
    optionalPrompt,
    postCallHold: postCallHold && locked,
    attemptUnlock,
    dismissOptionalPrompt,
    acceptOptionalPrompt,
    signOut: signOutFromLock,
  }
}
